import React from "react";
import { ShopingGuideCtaCard } from "@aph/ui-lib-humanx";
import { useRouter } from "next/navigation";
import { NEXT_PUBLIC_PHARMACY_BASE_URL, NEXT_PUBLIC_PHARMACY_MED_IMAGES_BASE_URL } from "@/libs/constants/auth";
import updatingCartSelector from "@/redux/slice/cart/selectors/updatingCart";
import { useAppSelector, useAppDispatch } from "@/redux/hooks";
import { formatPrice } from "@/redux/slice/cart/utils/formatPrice";
import { getSkuQuantity } from "@/redux/slice/cart/utils/getSkuQuantity";
import { cartItemsSelector } from "@/redux/slice/cart/selectors/cartItems";
import { CartItem } from "@/redux/slice/cart/types/CartItem";
import { addCartItem } from "@/redux/slice/cart/utils/addCartItem";
import currentPatientIdSelector from "@/redux/slice/auth/selectors/currentPatientId";
import pharmaAddressDetailsSelector from "@/redux/slice/location/selectors/pharmaAddressDetails";
import { updateCartItem } from "@/redux/slice/cart/utils/updateCartItem";
import { removeCartItem } from "@/redux/slice/cart/utils/removeCartItem";
import Slider, { Slide } from "@/components/Slider";
import { DeviceInfo } from "@/interface/deviceInfo";
import IconRight from "$/icons/icon-right.svg";
import IconLeft from "$/icons/icon-left.svg";
import { GAAddToCartEvent } from "@/libs/trackingHelpers/GAAddToCartEvent";
import { getRedirectUrl } from "@/components/ShoppingComponent/constant";
import { GAProductClickedEvent } from "@/libs/trackingHelpers/GAProductClickedEvent";
import { assetClickedEvent } from "@/helpers/TSDEvents";
import { NON_ONBOARDED_STATUS, ONBOARDING_STATUS } from "@/helpers/constants";
import SegmentInfoSelector from "@/redux/slice/auth/selectors/userSegmentInfo";
import { getPlainTexts } from "@/helpers";
import styles from "./ShoppingWidget2.module.scss";
import ButtonLink from "@/components/ButtonLink";

interface ShoppingWidget2Props {
  userAgent?: DeviceInfo;
  products?: CartItem[];
  desktopItems?: number;
  assetPosition?: number;
  sectionName?: any;
  isPharma?: boolean;
  data?: any;
}

const ShoppingWidget2: React.FC<ShoppingWidget2Props> = (props) => {
  const { userAgent, products, desktopItems = 3, assetPosition, sectionName, isPharma = false, data } = props;
  const dispatch = useAppDispatch();
  const router = useRouter();
  const updatingCart = useAppSelector(updatingCartSelector);
  const cartItems = useAppSelector(cartItemsSelector);
  const pharmaAddressDetails = useAppSelector(pharmaAddressDetailsSelector);
  const currentPatientId = useAppSelector(currentPatientIdSelector);
  const SegmentInfoObject = useAppSelector(SegmentInfoSelector);

  const handleAddItemToCart = (product) => {
    if (product?.is_in_stock) {
      const cartItem: CartItem = {
        ...product,
        quantity: Number(product.moq),
      };
      GAAddToCartEvent(cartItem);
      addCartItem({ itemToAdd: cartItem, pharmaAddressDetails, currentPatientId, dispatch });
    }
  };

  const handleQuantityDecrement = (product) => {
    const medicineQtyInCart = getSkuQuantity(product?.sku, cartItems);
    const cartItem: CartItem = {
      ...product,
      quantity: -1,
    };

    if (medicineQtyInCart === Number(product?.moq)) {
      removeCartItem({ sku: product?.sku, currentPatientId, cartItems, dispatch });
    } else {
      updateCartItem({ itemUpdates: cartItem, cartItems, currentPatientId, dispatch });
    }
  };

  const handleQuantityIncrement = (product) => {
    const medicineQtyInCart = getSkuQuantity(product?.sku, cartItems);

    if (medicineQtyInCart < Number(product?.MaxOrderQty || 10)) {
      const cartItem: CartItem = {
        ...product,
        quantity: 1,
      };
      updateCartItem({ itemUpdates: cartItem, cartItems, currentPatientId, dispatch });
    }
  };

  const handleCardClick = (product) => {
    GAProductClickedEvent(product);
    router.push(getRedirectUrl(product));
  };

  return (
    <>
      <div className={`spacingBetweenSections ${styles.root}`}>
        <Slider
          autoplay={false}
          showArrows={userAgent?.isDesktop}
          slidesToShow={userAgent?.isDesktop ? desktopItems : 1}
          columnGap={userAgent?.isDesktop ? 14 : 20}
          nextArrow={<IconRight className={`${styles.arrows} ${styles.arrowNext}`} />}
          prevArrow={<IconLeft className={`${styles.arrows} ${styles.arrowPrev}`} />}
          showDots={true}
          slidesToScroll={1}
        >
          {products?.map((product, index) => {
            const { name = "", price, special_price, sku = "", thumbnail } = product;

            const medicineQtyInCart = getSkuQuantity(sku, cartItems);

            return (
              <Slide key={product?.sku} index={index}>
                <ShopingGuideCtaCard
                  key={product?.sku}
                  title={name}
                  imageUrl={`${NEXT_PUBLIC_PHARMACY_MED_IMAGES_BASE_URL}${thumbnail}`}
                  originalPrice={formatPrice(price)}
                  actualPrice={formatPrice(Number(special_price))}
                  ctaText="Add"
                  quantity={medicineQtyInCart}
                  decreaseItemQty={() => {
                    handleQuantityDecrement(product);
                  }}
                  increaseItemQty={() => {
                    handleQuantityIncrement(product);
                  }}
                  addCta={() => {
                    handleAddItemToCart(product);
                  }}
                  onClick={() => {
                    assetClickedEvent({
                      asset_name: getPlainTexts(sectionName),
                      asset_display_text: name,
                      asset_position: assetPosition,
                      asset_element_position: index + 1,
                      momverse_stage: SegmentInfoObject?.type
                        ? (ONBOARDING_STATUS[SegmentInfoObject?.type] ?? "")
                        : NON_ONBOARDED_STATUS,
                    });
                    handleCardClick(product);
                  }}
                  redirectUrl={getRedirectUrl(product)}
                />
              </Slide>
            );
          })}
        </Slider>
      </div>
      {data?.ViewAllProductsUrl && isPharma && (
          <ButtonLink color="tertiarybaby" href={data?.ViewAllProductsUrl} className={styles.btn}>
            Load more products
          </ButtonLink>
        )}
    </>
  );
};

export default ShoppingWidget2;
